import React, { useState, useEffect } from "react";
import { Upload, Download, ArrowLeft, Folder, FileText, BarChart2, ShieldCheck, Activity, Trash2, Loader2, AlertCircle, CheckCircle, XCircle } from "lucide-react";
// Integrating your service functions
import { getEmployeeDocuments, adminDownloadDoc, deleteEmployeeDocument, employeeUploadDocument } from '../services/employee.service';

const folderStyles = {
  "Personal Documents": { bg: "bg-pink-50", accent: "bg-pink-500", text: "text-pink-700", border: "border-pink-200" },
  "Company Letters": { bg: "bg-blue-50", accent: "bg-blue-500", text: "text-blue-700", border: "border-blue-200" },
  "Education Certificates": { bg: "bg-emerald-50", accent: "bg-emerald-500", text: "text-emerald-700", border: "border-emerald-200" },
  "Experience Letters": { bg: "bg-amber-50", accent: "bg-amber-500", text: "text-amber-700", border: "border-amber-200" },
  "Id Proofs": { bg: "bg-purple-50", accent: "bg-purple-500", text: "text-purple-700", border: "border-purple-200" },
};

const folders = ["Personal Documents", "Company Letters", "Education Certificates", "Experience Letters", "Id Proofs"];

export default function EmployeeDocuments() {
  const [activeFolder, setActiveFolder] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Get employee code dynamically from localStorage
  const empCode = localStorage.getItem("empCode") || localStorage.getItem("emp_code");

  useEffect(() => {
    const fetchDocs = async () => {
      console.log("\n" + "=".repeat(60));
      console.log("📂 [FRONTEND] Fetching documents for employee:", empCode);
      console.log("=".repeat(60));
      
      if (!empCode) {
        console.error("❌ [FRONTEND] No employee code found in localStorage");
        console.error("   Checked keys: empCode, emp_code");
        console.error("   Available localStorage keys:", Object.keys(localStorage));
        setError("User session not found. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log("🔗 [FRONTEND] Calling API: getEmployeeDocuments(", empCode, ")");
        
        const response = await getEmployeeDocuments(empCode);
        console.log("✅ [FRONTEND] API Response received:", response);
        console.log("   Response type:", typeof response);
        console.log("   Is Array:", Array.isArray(response));
        
        // Backend returns array directly
        const data = Array.isArray(response) ? response : (response?.data || []);
        console.log("📊 [FRONTEND] Processing", data.length, "documents");
        
        const mappedData = data.map(item => {
          const doc = {
            id: item.id,
            name: item.file_name || "Untitled Document",
            category: item.category || "Personal Documents",
            status: item.status || "PENDING",
            s3Key: item.s3_key,
            rejection_reason: item.rejection_reason
          };
          console.log("   📄 Document:", doc.name, "-", doc.category, "-", doc.status);
          return doc;
        });

        setDocs(mappedData);
        setError(null);
        console.log("✅ [FRONTEND] Documents loaded successfully!");
        console.log("=".repeat(60) + "\n");
      } catch (err) {
        console.error("\n" + "=".repeat(60));
        console.error("❌ [FRONTEND] Error fetching documents:");
        console.error("   Error object:", err);
        console.error("   Error message:", err.message);
        console.error("   Error response:", err.response);
        console.error("=".repeat(60) + "\n");
        setError("Failed to load documents. The server may be down or the path is incorrect.");
      } finally {
        setLoading(false);
      }
    };

    fetchDocs();
  }, [empCode]);

  // --- ACTIONS ---
  const handleSecureDownload = async (docId, fileName) => {
    console.log("\n" + "=".repeat(60));
    console.log("⬇️ [FRONTEND] Downloading document:");
    console.log("   Document ID:", docId);
    console.log("   File Name:", fileName);
    console.log("   Employee Code:", empCode);
    console.log("=".repeat(60));
    
    try {
      console.log("🔗 [FRONTEND] Calling API: adminDownloadDoc(", empCode, ",", docId, ")");
      const response = await adminDownloadDoc(empCode, docId);
      console.log("✅ [FRONTEND] Download API Response:", response);
      
      // Backend returns: {"download_url": "https://..."}
      const downloadUrl = response.data?.download_url || response.download_url;
      console.log("🔗 [FRONTEND] Download URL:", downloadUrl);
      
      if (downloadUrl) {
        console.log("✅ [FRONTEND] Opening download URL in new tab");
        window.open(downloadUrl, "_blank");
        console.log("=".repeat(60) + "\n");
      } else {
        console.error("❌ [FRONTEND] No download URL in response");
        throw new Error("Download URL not generated");
      }
    } catch (err) {
      console.error("\n" + "=".repeat(60));
      console.error("❌ [FRONTEND] Download failed:");
      console.error("   Error:", err);
      console.error("=".repeat(60) + "\n");
      alert("Download failed. Your session might have expired.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      try {
        await deleteEmployeeDocument(empCode, id);
        setDocs(docs.filter(doc => doc.id !== id));
        alert('Document deleted successfully');
      } catch (err) {
        alert('Failed to delete document: ' + err.message);
      }
    }
  };

  const handleUpload = async (e) => {
    if (!e.target.files?.[0] || !activeFolder) return;
    const file = e.target.files[0];
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", activeFolder);

    setUploading(true);
    try {
      await employeeUploadDocument(empCode, formData);
      // Refresh documents
      const response = await getEmployeeDocuments(empCode);
      const data = Array.isArray(response) ? response : (response?.data || []);
      const mappedData = data.map(item => ({
        id: item.id,
        name: item.file_name || "Untitled Document",
        category: item.category || "Personal Documents",
        status: item.status || "PENDING",
        s3Key: item.s3_key,
        rejection_reason: item.rejection_reason
      }));
      setDocs(mappedData);
      alert('Document uploaded successfully! Awaiting admin approval.');
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset file input
    }
  };

  // --- UI CALCULATIONS ---
  const totalDocs = docs.length;
  const requiredDocs = 10; // Adjusted based on standard HR requirements
  const complianceScore = Math.min(Math.round((totalDocs / requiredDocs) * 100), 100);

  const getComplianceStyle = (score) => {
    if (score >= 80) return { ring: "text-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", gradient: "from-emerald-400 to-emerald-600" };
    if (score >= 50) return { ring: "text-amber-500", text: "text-amber-700", bg: "bg-amber-50", gradient: "from-amber-400 to-amber-600" };
    return { ring: "text-rose-500", text: "text-rose-700", bg: "bg-rose-50", gradient: "from-rose-400 to-rose-600" };
  };

  const complianceStyle = getComplianceStyle(complianceScore);

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F3F4F6]">
      <Loader2 className="animate-spin text-indigo-600" size={48} />
      <p className="mt-4 font-medium text-gray-600 tracking-wide">Securing connection to Vault...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F3F4F6] p-6 text-center">
      <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 max-w-sm">
        <AlertCircle className="text-rose-500 mb-4 mx-auto" size={64} />
        <h2 className="text-xl font-bold text-gray-800 font-poppins">{error}</h2>
        <p className="text-gray-500 mt-2 text-sm leading-relaxed">Ensure the backend is running and your employee ID is valid.</p>
        <button onClick={() => window.location.reload()} className="mt-6 w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all">Retry Now</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F3F4F6] p-4 md:p-8 font-roboto">
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&family=Roboto:wght@400;500&display=swap');
        .font-poppins { font-family: 'Poppins', sans-serif; }
      `}} />

      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 font-poppins">Document Vault</h1>
            <p className="text-sm text-gray-500 mt-1 uppercase tracking-widest font-bold">Emp Code: <span className="text-indigo-600">{empCode}</span></p>
          </div>
          <div className="flex gap-3">
             <button className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-5 py-2.5 rounded-xl hover:bg-gray-50 transition-all text-sm font-bold shadow-sm">
                <ShieldCheck size={18} className="text-indigo-500" /> Compliance Report
             </button>
          </div>
        </div>

        {!activeFolder && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
              <div className={`lg:col-span-4 ${complianceStyle.bg} p-6 rounded-3xl shadow-sm border border-white flex flex-col items-center justify-center`}>
                <h3 className="text-[10px] font-bold text-gray-400 uppercase mb-5 tracking-widest">Compliance Score</h3>
                <div className="relative">
                  <svg className="w-36 h-36 transform -rotate-90">
                    <circle className="text-gray-200/50" strokeWidth="10" stroke="currentColor" fill="transparent" r="54" cx="72" cy="72" />
                    <circle
                      className={`${complianceStyle.ring} transition-all duration-1000 ease-out`}
                      strokeWidth="10" strokeDasharray="339.29"
                      strokeDashoffset={339.29 - (339.29 * complianceScore) / 100}
                      strokeLinecap="round" stroke="currentColor" fill="transparent" r="54" cx="72" cy="72"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-3xl font-bold ${complianceStyle.text}`}>{complianceScore}%</span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-8 bg-white p-6 rounded-3xl shadow-sm border border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <h3 className="text-[10px] font-bold text-gray-400 uppercase mb-4 tracking-widest flex items-center gap-2">
                    <BarChart2 size={14} /> Category Health
                  </h3>
                  <div className="space-y-4">
                    {folders.map(folder => {
                      const count = docs.filter(d => d.category === folder).length;
                      const percentage = totalDocs > 0 ? (count / totalDocs) * 100 : 0;
                      return (
                        <div key={folder}>
                          <div className="flex justify-between text-[11px] mb-1.5">
                            <span className="font-bold text-gray-500 uppercase">{folder}</span>
                            <span className="font-bold text-gray-900">{count}</span>
                          </div>
                          <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                            <div className={`${folderStyles[folder].accent} h-full rounded-full transition-all duration-1000`} style={{ width: `${percentage}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="bg-indigo-600 rounded-2xl p-6 text-white flex flex-col justify-between">
                  <FileText size={32} className="opacity-40" />
                  <div>
                    <p className="text-2xl font-bold">{totalDocs}</p>
                    <p className="text-[10px] font-bold uppercase opacity-80">Files in Cloud</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {folders.map((folder) => {
                const fileCount = docs.filter(d => d.category === folder).length;
                const style = folderStyles[folder];
                return (
                  <div key={folder} onClick={() => setActiveFolder(folder)} className={`${style.bg} border border-transparent p-5 rounded-3xl cursor-pointer hover:border-indigo-200 hover:shadow-xl hover:-translate-y-1 transition-all group`}>
                    <div className={`w-12 h-12 rounded-2xl ${style.accent} text-white flex items-center justify-center mb-4 shadow-lg shadow-indigo-100 group-hover:rotate-6 transition-transform`}><Folder size={24} /></div>
                    <h3 className={`font-bold text-sm leading-tight ${style.text} mb-1`}>{folder}</h3>
                    <p className="text-[10px] text-gray-400 font-bold uppercase">{fileCount} Items</p>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {activeFolder && (
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-8 animate-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center justify-between mb-8 border-b border-gray-50 pb-6">
              <div className="flex items-center gap-4">
                <button onClick={() => setActiveFolder(null)} className="p-3 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-all text-gray-500"><ArrowLeft size={20} /></button>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 font-poppins">{activeFolder}</h2>
                  <p className="text-xs text-gray-400 font-medium">{docs.filter(d => d.category === activeFolder).length} files found</p>
                </div>
              </div>
              <label className={`flex items-center gap-2 cursor-pointer bg-indigo-600 text-white px-6 py-3 rounded-2xl hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all text-sm font-bold uppercase tracking-wide ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                {uploading ? <><Loader2 className="animate-spin" size={18} /> Uploading...</> : <><Upload size={18} /> Upload New</>}
                <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {docs.filter(d => d.category === activeFolder).map(doc => (
                <div key={doc.id} className="bg-white p-4 rounded-2xl border border-gray-100 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`p-3 rounded-xl ${folderStyles[activeFolder].bg} ${folderStyles[activeFolder].text}`}><FileText size={20} /></div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-gray-800 truncate max-w-[150px] md:max-w-xs">{doc.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {doc.status === "ACCEPTED" ? (
                            <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full"><CheckCircle size={10} /> Verified</span>
                          ) : doc.status === "REJECTED" ? (
                            <span className="flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full"><XCircle size={10} /> Rejected</span>
                          ) : (
                            <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full tracking-wider italic">Review Pending</span>
                          )}
                        </div>
                        {doc.status === "REJECTED" && doc.rejection_reason && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-[10px] font-bold text-red-700 mb-1 uppercase tracking-wide">Rejection Reason:</p>
                            <p className="text-xs text-red-600">{doc.rejection_reason}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleSecureDownload(doc.id, doc.name)} className="p-2.5 bg-gray-50 text-gray-500 rounded-xl hover:bg-indigo-50 hover:text-indigo-600 transition-all" title="Download">
                        <Download size={18} />
                      </button>
                      {doc.status === "REJECTED" && (
                        <button onClick={() => handleDelete(doc.id)} className="p-2.5 bg-gray-50 text-gray-500 rounded-xl hover:bg-rose-50 hover:text-rose-600 transition-all" title="Delete">
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {docs.filter(d => d.category === activeFolder).length === 0 && (
                <div className="col-span-full py-20 text-center text-gray-400">
                  <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Folder size={32} className="opacity-20" />
                  </div>
                  <h3 className="font-bold text-gray-500">Folder is empty</h3>
                  <p className="text-xs text-gray-400 mt-1">Upload files to populate this category.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}